import ApiCaller from '../utils/apiCaller'
import Toggles from '@domain/toggles/Toggles'
import {
    TEMPORAL_VARIATIONS_TOGGLE,
    mergeTemporalOptionsIntoModelData,
    superposeTemporalVariations,
} from '@domain/catalog/TemporalVariation'

/**
 * DV-6834: temporal variations (ERP `BOV = 'T'`, special conditions with a validity window of days) for the users
 * enabled by toggle. The public catalog the configurators read (byFamily JSON, v2 articles/variations) never has
 * them: for an enabled user, web-api v3 sends them as offers and this module superposes them on the subfamily data
 * (the options) and on the ArticleCollection (reference and price). For everyone else no call is made and the
 * data comes back as it was.
 *
 * The offers fetched for a sub-family are kept for the browser session, so the saves of the same configurator
 * (which build their own ArticleCollection) see the same temporal variations. The memory is the user's browser:
 * nothing is shared between users.
 */

export interface TemporalOffer {
    model: string
    optionsKey: 'finishes' | 'temperatures' | 'controls' | 'installations'
    variation: any
    option: any
}

/** The validity of a temporal option, when the option is one. */
interface OptionWithValidity {
    type?: string
    temporal?: { from?: string | null; to?: string | null }
}

const offersBySubfamily = new Map<string, TemporalOffer[]>()

const subfamilyKey = (catalog: number | string, subfamily: number | string | undefined) => `${catalog}|${subfamily ?? ''}`

/** Whether the `temporalVariations` toggle targets the user of this email. */
export function isTemporalSelectionEnabled(email?: string | null): boolean {
    if (!email) return false
    return Toggles.isEnabledForEmail(TEMPORAL_VARIATIONS_TOGGLE, email)
}

/**
 * The temporal variations the signed-in user can choose for these models, or `[]` (user not enabled, nothing
 * valid, or web-api unreachable — the configurator then works exactly as without the feature).
 */
export async function fetchTemporalOffers(models: Array<string | number>, catalog: number | string, subfamily?: number | string, email?: string | null): Promise<TemporalOffer[]> {
    const uniqueModels = Array.from(new Set(models.map(String).filter(Boolean)))
    if (uniqueModels.length === 0 || !isTemporalSelectionEnabled(email)) return []
    const query = [`catalog=${catalog}`, ...uniqueModels.map((model) => `model=${encodeURIComponent(model)}`)]
    if (subfamily !== undefined && subfamily !== null && subfamily !== '') query.push(`subfamily=${subfamily}`)
    try {
        const response = await ApiCaller.call('v3', 'GET', `catalog/temporal-variations?${query.join('&')}`, null, { credentials: 'include' })
        const offers: TemporalOffer[] = Array.isArray(response?.offers) ? response.offers : []
        offersBySubfamily.set(subfamilyKey(catalog, subfamily), offers)
        return offers
    } catch (error) {
        console.warn('[configurator] temporal variations unavailable; configuring without them', error)
        return []
    }
}

/** The offers last fetched in this browser session for a sub-family ([] when none). */
export function rememberedTemporalOffers(catalog: number | string, subfamily?: number | string): TemporalOffer[] {
    return offersBySubfamily.get(subfamilyKey(catalog, subfamily)) ?? []
}

/** Sub-family data (`{ [model]: entry }`) with the temporal options of each offered model. Same object without offers. */
export function withTemporalOptions<T extends Record<string, any>>(subfamilyData: T, offers: TemporalOffer[]): T {
    if (!subfamilyData || !offers.length) return subfamilyData
    const result: Record<string, any> = { ...subfamilyData }
    for (const model of new Set(offers.map((offer) => offer.model))) {
        if (result[model]) result[model] = mergeTemporalOptionsIntoModelData(result[model], offers as any)
    }
    return result as T
}

/** A new ArticleCollection with the temporal variations (the given one is left as it is). */
export function withTemporalVariations(articleCollection: any, offers: TemporalOffer[]): any {
    if (!articleCollection || !offers.length) return articleCollection
    return superposeTemporalVariations(articleCollection, offers.map((offer) => offer.variation))
}

/** `dd/mm/yyyy` of the last valid day of a temporal option; null for any other option. */
export function temporalOptionUntil(option: OptionWithValidity): string | null {
    const to = option?.type === 'temporal' ? option.temporal?.to : null
    if (!to) return null
    const [year, month, day] = to.split('-')
    return year && month && day ? `${day}/${month}/${year}` : to
}
